import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-semibold cursor-pointer transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-primary-s500 text-white hover:bg-primary-s500/90 active:bg-primary-s500/80 rounded-3xl',
        primary:
          'bg-primary-s500 text-white hover:bg-primary-s500/90 active:bg-primary-s500/80 rounded-3xl',
        secondary:
          'bg-secondary-s500 text-white hover:bg-secondary-s500/90 active:bg-secondary-s500/80 rounded-3xl',
        light:
          'bg-primary-s50 text-primary-s500 hover:bg-primary-s50/80 active:bg-primary-s50/60 rounded-3xl',
        outline:
          'bg-white text-primary-s500 border border-smoke hover:border-primary-s500 hover:bg-neutral-background active:bg-neutral-disabled rounded-3xl',
        ghost:
          'hover:bg-neutral-background active:bg-neutral-disabled rounded-3xl',
        link: 'text-primary-s500 underline-offset-4 hover:underline',
        destructive:
          'bg-danger text-white hover:bg-danger/90 active:bg-danger/80 rounded-3xl',
      },
      size: {
        default: 'h-11 px-6 py-2',
        sm: 'h-9 px-4 rounded-3xl',
        lg: 'h-14 px-8 rounded-3xl',
        icon: 'h-11 w-11',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
